import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

const DictationStep = ({ formData, onInputChange }) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [interimTranscript, setInterimTranscript] = useState('');

  useEffect(() => {
    // Check if browser supports Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window?.SpeechRecognition || window?.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'fr-FR';

      recognitionInstance.onresult = (event) => {
        let interim = '';
        let final = '';

        for (let i = event?.resultIndex; i < event?.results?.length; i++) {
          const transcript = event?.results?.[i]?.[0]?.transcript;
          if (event?.results?.[i]?.isFinal) {
            final += transcript + ' ';
          } else {
            interim += transcript;
          }
        }

        if (final) {
          const currentNotes = formData?.sourceNotes || '';
          onInputChange({ target: { name: 'sourceNotes', value: currentNotes + final } });
          setInterimTranscript('');
        } else {
          setInterimTranscript(interim);
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event?.error);
        setIsListening(false);
        
        if (event?.error === 'not-allowed') {
          toast?.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres du navigateur.');
        } else if (event?.error === 'no-speech') {
          toast?.error('Aucune parole détectée. Veuillez réessayer.');
        } else {
          toast?.error('Erreur de reconnaissance vocale');
        }
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
        setInterimTranscript('');
      };

      setRecognition(recognitionInstance);
    }

    return () => {
      if (recognition) {
        recognition?.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!recognition) {
      toast?.error('La reconnaissance vocale n\'est pas supportée par votre navigateur');
      return;
    }

    if (isListening) {
      recognition?.stop();
      setIsListening(false);
      toast?.success('Dictée arrêtée');
    } else {
      recognition?.start();
      setIsListening(true);
      toast?.success('Écoute en cours... Parlez maintenant');
    }
  };

  const handleTextChange = (e) => {
    onInputChange({ target: { name: 'sourceNotes', value: e?.target?.value } });
  };

  const clearNotes = () => {
    onInputChange({ target: { name: 'sourceNotes', value: '' } });
    setInterimTranscript('');
    toast?.success('Notes effacées');
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Dictée vocale</h2>
        <p className="text-gray-400">Dictez votre compte rendu comme vous le feriez à l'oral</p>
      </div>

      {/* Large Voice Button */}
      <div className="flex flex-col items-center justify-center py-12">
        <button
          type="button"
          onClick={toggleListening}
          className={`
            relative w-48 h-48 rounded-full transition-all duration-300 transform
            ${isListening
              ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-2xl shadow-red-500/50 scale-105'
              : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-xl shadow-blue-500/30 hover:scale-105'
            }
          `}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Icon 
              name={isListening ? 'MicOff' : 'Mic'} 
              size={64} 
              className="text-white mb-3"
            />
            <span className="text-white font-bold text-xl">
              {isListening ? 'Arrêter' : 'Parler'}
            </span>
          </div>
          {isListening && (
            <div className="absolute inset-0 rounded-full border-4 border-red-300 animate-ping opacity-75"></div>
          )}
        </button>

        {isListening && (
          <div className="mt-6 flex items-center gap-2 text-red-400 animate-pulse">
            <span className="w-3 h-3 bg-red-400 rounded-full"></span>
            <span className="font-medium">Écoute en cours...</span>
          </div>
        )}
      </div>

      {/* Live Transcript Display */}
      {(formData?.sourceNotes || interimTranscript) && (
        <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Icon name="FileText" size={20} className="text-blue-400" />
              Transcription
            </h3>
            <button
              type="button"
              onClick={clearNotes}
              className="text-sm text-red-400 hover:text-red-300 transition-colors"
            >
              <Icon name="Trash2" size={16} className="inline mr-1" />
              Effacer
            </button>
          </div>

          <textarea
            value={formData?.sourceNotes}
            onChange={handleTextChange}
            placeholder="Vos notes apparaîtront ici..."
            rows={12}
            className="w-full px-4 py-3 bg-gray-900/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 resize-none"
          />

          {interimTranscript && (
            <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-300 italic">{interimTranscript}</p>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-2">
            Vous pouvez également modifier le texte directement
          </p>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Icon name="Info" size={18} className="text-blue-400" />
          Conseils pour la dictée
        </h4>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-green-400 mt-0.5" />
            <span>Parlez naturellement comme si vous dictiez à un assistant</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-green-400 mt-0.5" />
            <span>L'IA structurera automatiquement votre dictée en compte rendu professionnel</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-green-400 mt-0.5" />
            <span>Vous pourrez relire et corriger le résultat à l'étape suivante</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DictationStep;